/**
 * Checkout Hook using TanStack Query
 * Replaces the coordinator pattern with automatic cache invalidation
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CheckoutData, CheckoutInitParams, CheckoutLineItem, CheckoutResource, CheckoutSessionPreview } from '../../core/resources/checkout';
import { getAssignedOrderBumpOfferIds } from '../../core/funnelClient';
import { useTagadaContext } from '../providers/TagadaProvider';

import { getGlobalApiClient } from './useApiQuery';
import { useCurrency } from './useCurrency';
import { usePluginConfig } from './usePluginConfig';

export interface CheckoutMessages {
    /** Error shown when CMS session takes too long to initialize. Default: 'Session initialization timeout. Please refresh the page and try again.' */
    sessionTimeout?: string;
    /** Error shown when checkout resource fails to initialize. Default: 'Failed to initialize checkout resource' */
    initFailed?: string;
    /** Error shown when an operation is attempted without an active checkout session. Default: 'No checkout session available' */
    noCheckoutSession?: string;
}

const DEFAULT_MESSAGES = {
    sessionTimeout: 'Session initialization timeout. Please refresh the page and try again.',
    initFailed: 'Failed to initialize checkout resource',
    noCheckoutSession: 'No checkout session available',
} as const satisfies Required<CheckoutMessages>;

export interface UseCheckoutQueryOptions {
    checkoutToken?: string;
    enabled?: boolean;
    /** Override default error messages (e.g. for i18n). Use with useTranslation's t() to pass translated strings. */
    messages?: CheckoutMessages;
}

export interface UseCheckoutQueryResult {
    // Query data
    checkout: CheckoutData | undefined;
    isLoading: boolean;
    error: Error | null;
    isSuccess: boolean;

    // Actions
    init: (params: CheckoutInitParams) => Promise<{ checkoutSession: any; checkoutToken: string }>;
    refresh: () => Promise<void>;

    // Checkout operations
    replaceSessionLineItems: (lineItems: CheckoutLineItem[]) => Promise<any>;
    updateLineItems: (lineItems: CheckoutLineItem[]) => Promise<any>;
    updateLineItemsOptimistic: (lineItems: CheckoutLineItem[]) => void;
    addLineItems: (lineItems: CheckoutLineItem[]) => Promise<any>;
    removeLineItems: (lineItems: { variantId: string; quantity?: number }[]) => Promise<any>;
    setItemQuantity: (variantId: string, quantity: number, priceId?: string) => Promise<any>;
    updateCustomer: (data: { email: string; acceptsMarketing?: boolean }) => Promise<any>;
    updateCustomerAndSessionInfo: (data: { customerData: { email: string; acceptsMarketing?: boolean }; shippingAddress: any; billingAddress?: any; differentBillingAddress?: boolean }) => Promise<any>;
    applyPromotionCode: (code: string) => Promise<any>;
    removePromotion: (promotionId: string) => Promise<any>;
    previewCheckoutSession: (lineItems: CheckoutLineItem[], promotionIds?: string[]) => Promise<{ success: boolean; preview: CheckoutSessionPreview }>;
}

export function useCheckoutQuery(options: UseCheckoutQueryOptions = {}): UseCheckoutQueryResult {
    const { checkoutToken: providedToken, enabled = true, messages: userMessages } = options;
    const messages = { ...DEFAULT_MESSAGES, ...userMessages };
    const { storeId } = usePluginConfig();
    const currency = useCurrency();
    const queryClient = useQueryClient();
    const { isSessionInitialized, session } = useTagadaContext();

    // Track pending session promises to avoid creating multiple promises
    const pendingSessionPromise = useRef<Promise<void> | null>(null);
    const sessionResolvers = useRef<Set<() => void>>(new Set());

    // Resolve all pending promises when session becomes ready
    useEffect(() => {
        if (isSessionInitialized && sessionResolvers.current.size > 0) {
            console.log('✅ [useCheckout] CMS session ready, resolving pending promises');
            sessionResolvers.current.forEach(resolve => resolve());
            sessionResolvers.current.clear();
            pendingSessionPromise.current = null;
        }
    }, [isSessionInitialized]);

    // Clean, event-driven session waiting - no polling needed
    const waitForSession = useCallback(async (): Promise<void> => {
        if (isSessionInitialized) {
            return Promise.resolve();
        }

        // Reuse existing promise if one is pending
        if (pendingSessionPromise.current) {
            return pendingSessionPromise.current;
        }

        console.log('⏳ [useCheckout] Waiting for CMS session to be initialized...');

        // Create new promise that will be resolved by useEffect above
        pendingSessionPromise.current = new Promise<void>((resolve, reject) => {
            // Add resolver to set for useEffect to call
            sessionResolvers.current.add(resolve);

            // Safety timeout
            setTimeout(() => {
                if (sessionResolvers.current.has(resolve)) {
                    sessionResolvers.current.delete(resolve);
                    if (sessionResolvers.current.size === 0) {
                        pendingSessionPromise.current = null;
                    }
                    reject(new Error(messages.sessionTimeout));
                }
            }, 10000);
        });

        return pendingSessionPromise.current;
    }, [isSessionInitialized]);

    // Create checkout resource client
    const checkoutResource = useMemo(() => {
        try {
            return new CheckoutResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(messages.initFailed + ': ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Internal token state that can be updated after init
    const [internalToken, setInternalToken] = useState<string | undefined>(providedToken);

    // Update internal token when provided token changes
    useEffect(() => {
        if (providedToken && providedToken !== internalToken) {
            setInternalToken(providedToken);
        }
    }, [providedToken, internalToken]);

    // Use provided token or internal token
    const checkoutToken = providedToken || internalToken;


    // Only send currency to the backend when explicitly set via URL param or persisted storage.
    // When not explicit, the backend uses the session's own selectedPresentmentCurrency --
    // the frontend then reads the currency from the response, never guesses.
    const explicitCurrency = currency.isExplicit ? currency.code : undefined;

    // Main checkout query
    const {
        data: checkout,
        isLoading,
        error,
        isSuccess,
        refetch,
    } = useQuery<CheckoutData>({
        queryKey: ['checkout', checkoutToken, explicitCurrency],
        queryFn: () => checkoutResource.getCheckout(checkoutToken!, explicitCurrency),
        enabled: enabled && !!checkoutToken && isSessionInitialized,
        staleTime: 30_000, // 30 seconds
        refetchOnWindowFocus: false,
    });


    // Refresh function
    const refresh = useCallback(async () => {
        if (checkoutToken) {
            await refetch();
        }
    }, [refetch, checkoutToken]);

    // Initialize checkout mutation (async mode for fast response)
    const initMutation = useMutation<
        { checkoutSession: any; checkoutToken: string },
        Error,
        CheckoutInitParams
    >({
        mutationFn: async (params) => {
            const enabledOrderBumpOfferIds = params.enabledOrderBumpOfferIds ?? getAssignedOrderBumpOfferIds();
            const requestBody = {
                ...params,
                storeId: params.storeId || storeId,
                returnUrl: params.returnUrl || window.location.origin,
                customerId: params.customerId || session?.customerId,
                customer: {
                    ...params.customer,
                    currency: params.customer?.currency ?? currency.code,
                },
                ...(enabledOrderBumpOfferIds && { enabledOrderBumpOfferIds }),
            };

            // Use async mode for fast response (~50ms vs 2-5s)
            const asyncResponse = await checkoutResource.initCheckoutAsync(requestBody);

            return {
                checkoutToken: asyncResponse.checkoutToken,
                checkoutSession: {}, // Will be populated when getCheckout is called
            };
        },
        onSuccess: (response) => {
            // Update URL with checkout token
            if (typeof window !== 'undefined' && response.checkoutToken) {
                const currentUrl = new URL(window.location.href);
                if (!currentUrl.searchParams.has('checkoutToken')) {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('checkoutToken', response.checkoutToken);
                    window.history.replaceState(null, '', newUrl.toString());
                }
            }
        },
    });

    // Order bump functionality removed - use useOrderBumpQuery instead

    // Replace session line items mutation (full rewrite)
    const replaceSessionLineItemsMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { lineItems: CheckoutLineItem[] }
    >({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.replaceSessionLineItems(checkout.checkoutSession.id, lineItems);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Line items mutation with optimistic updates
    const lineItemsMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { lineItems: CheckoutLineItem[] }
    >({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.updateLineItems(checkout.checkoutSession.id, lineItems);
        },
        onMutate: async ({ lineItems }) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['checkout', checkoutToken] });

            // Snapshot the previous value
            const previousCheckout = queryClient.getQueryData(['checkout', checkoutToken]);

            // Optimistically update the checkout data
            if (previousCheckout && checkout?.checkoutSession?.id) {
                const optimisticCheckout = {
                    ...previousCheckout,
                    checkoutSession: {
                        ...(previousCheckout as any).checkoutSession,
                        sessionLineItems: lineItems.map(item => ({
                            variantId: item.variantId,
                            quantity: item.quantity,
                            priceId: item.priceId,
                            isOrderBump: false, // Default for line items
                        })),
                    },
                };

                queryClient.setQueryData(['checkout', checkoutToken], optimisticCheckout);
            }

            // Return a context object with the snapshotted value
            return { previousCheckout };
        },
        onError: (err, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context && typeof context === 'object' && 'previousCheckout' in context) {
                queryClient.setQueryData(['checkout', checkoutToken], (context as any).previousCheckout);
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure we have the latest data
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Add line items mutation
    const addLineItemsMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { lineItems: CheckoutLineItem[] }
    >({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.addLineItems(checkout.checkoutSession.id, lineItems);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Remove line items mutation
    const removeLineItemsMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { lineItems: { variantId: string; quantity?: number }[] }
    >({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.removeLineItems(checkout.checkoutSession.id, lineItems);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Item quantity mutation
    const quantityMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { variantId: string; quantity: number; priceId?: string }
    >({
        mutationFn: ({ variantId, quantity, priceId }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.setItemQuantity(checkout.checkoutSession.id, variantId, quantity, priceId);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Customer update mutation
    const customerMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { email: string; acceptsMarketing?: boolean }
    >({
        mutationFn: (data) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.updateCustomer(checkout.checkoutSession.id, data);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Customer and session info update mutation
    const customerAndSessionMutation = useMutation<
        { success: boolean; errors?: Record<string, { message: string; type: string }>; shippingCountryChanged?: boolean; billingCountryChanged?: boolean },
        Error,
        { customerData: { email: string; acceptsMarketing?: boolean }; shippingAddress: any; billingAddress?: any; differentBillingAddress?: boolean }
    >({
        mutationFn: (data) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.updateCustomerAndSessionInfo(checkout.checkoutSession.id, data);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });

    // Promotion code mutation
    const promotionMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { code: string }
    >({
        mutationFn: ({ code }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.applyPromotionCode(checkout.checkoutSession.id, code);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
            if (checkout?.checkoutSession?.id) {
                void queryClient.invalidateQueries({ queryKey: ['promotions', checkout.checkoutSession.id] });
            }
        },
    });

    // Remove promotion mutation
    const removePromotionMutation = useMutation<
        { success: boolean; error?: any },
        Error,
        { promotionId: string }
    >({
        mutationFn: ({ promotionId }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }

            return checkoutResource.removePromotion(checkout.checkoutSession.id, promotionId);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
            if (checkout?.checkoutSession?.id) {
                void queryClient.invalidateQueries({ queryKey: ['promotions', checkout.checkoutSession.id] });
            }
        },
    });

    const previewCheckoutSessionMutation = useMutation<
        { success: boolean; preview: CheckoutSessionPreview },
        Error,
        { lineItems: CheckoutLineItem[]; promotionIds?: string[] }
    >({
        mutationFn: ({ lineItems, promotionIds }) =>
            checkoutResource.previewCheckoutSession({
                lineItems,
                storeId,
                currency: currency.code,
                promotionIds,
            }),
    });

    return {
        // Query data
        checkout,
        isLoading,
        error,
        isSuccess,

        // Actions
        init: async (params) => {
            // Wait for session to be initialized to ensure we have customerId
            await waitForSession();

            const result = await initMutation.mutateAsync(params);

            // Update internal token state so the query can fetch the checkout data
            // The query will automatically refetch when token changes, and getCheckout()
            // will automatically wait for async completion (via SDK skipAsyncWait=false)
            setInternalToken(result.checkoutToken);

            // Return immediately with token
            // checkoutSession will be populated by the query once background processing completes
            return {
                checkoutSession: checkout?.checkoutSession ?? {},
                checkoutToken: result.checkoutToken,
            };
        },
        refresh,

        // Checkout operations
        replaceSessionLineItems: (lineItems) => replaceSessionLineItemsMutation.mutateAsync({ lineItems }),
        updateLineItems: (lineItems) => lineItemsMutation.mutateAsync({ lineItems }),
        updateLineItemsOptimistic: (lineItems: CheckoutLineItem[]) => lineItemsMutation.mutate({ lineItems }),
        addLineItems: (lineItems) => addLineItemsMutation.mutateAsync({ lineItems }),
        removeLineItems: (lineItems) => removeLineItemsMutation.mutateAsync({ lineItems }),
        setItemQuantity: (variantId, quantity, priceId) =>
            quantityMutation.mutateAsync({ variantId, quantity, priceId }),
        updateCustomer: (data) => customerMutation.mutateAsync(data),
        updateCustomerAndSessionInfo: (data) => customerAndSessionMutation.mutateAsync(data),
        applyPromotionCode: (code) => promotionMutation.mutateAsync({ code }),
        removePromotion: (promotionId) => removePromotionMutation.mutateAsync({ promotionId }),
        previewCheckoutSession: (lineItems, promotionIds) =>
            previewCheckoutSessionMutation.mutateAsync({ lineItems, promotionIds }),
    };
}
